import { useState, useEffect, useCallback } from 'react';
import { Home, List, DollarSign, ArrowLeft, Bell } from 'lucide-react';
import axios from 'axios';
import { socket } from '../../admin/chat/socket';
import DriverExpressHome from './DriverExpressHome';
import DriverExpressList from './DriverExpressList';
import DriverExpressEarnings from './DriverExpressEarnings';
import DriverExpressMap from './DriverExpressMap';

const API_URL = process.env.REACT_APP_API_URL;

const TABS = [
  { id: 'home',     label: 'Inicio',    Icon: Home },
  { id: 'list',     label: 'Entregas',  Icon: List },
  { id: 'earnings', label: 'Ganancias', Icon: DollarSign },
];

function getTodayLocal() {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day   = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function DriverExpressDashboard({ user_id, setActiveComponent }) {
  const [activeTab, setActiveTab]       = useState('home');
  const [deliveries, setDeliveries]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayLocal());
  const [newAssignment, setNewAssignment] = useState(null);

  // ── Lifted here so DriverExpressMap renders OUTSIDE the
  //    overflow-y-auto container — fixes fixed positioning clip ──
  const [mapDelivery, setMapDelivery] = useState(null); // { delivery, focusOnPickup }

  // ── Fetch deliveries ──────────────────────────────────────
  const fetchDeliveries = useCallback(async (date) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/driver/express/deliveries`, {
        params: { user_id, date },
      });
      setDeliveries(data);
    } catch (err) {
      console.error('[DriverExpress] Error fetching deliveries:', err);
    } finally {
      setLoading(false);
    }
  }, [user_id]);

  useEffect(() => { fetchDeliveries(selectedDate); }, []);

  // ── Socket room ───────────────────────────────────────────
  useEffect(() => {
    socket.emit('join_driver_room', { user_id });
    return () => socket.emit('leave_driver_room', { user_id });
  }, [user_id]);

  // ── Socket: new delivery assigned ────────────────────────
  useEffect(() => {
    function onAssignedToMe(data) {
      setNewAssignment(data);
      if (data.scheduled_date === selectedDate) {
        setDeliveries(prev => {
          const exists = prev.find(d => d.id === data.delivery_id);
          if (exists) return prev;
          return [{ ...data, id: data.delivery_id }, ...prev];
        });
      }
    }
    socket.on('express_delivery_assigned_to_me', onAssignedToMe);
    return () => socket.off('express_delivery_assigned_to_me', onAssignedToMe);
  }, [selectedDate]);

  // ── Socket: status updated ────────────────────────────────
  useEffect(() => {
    function onStatusUpdated({ delivery_id, to_status, updated_at }) {
      setDeliveries(prev =>
        prev.map(d => d.id === delivery_id ? { ...d, status: to_status, updated_at } : d)
      );
    }
    socket.on('express_status_updated', onStatusUpdated);
    return () => socket.off('express_status_updated', onStatusUpdated);
  }, []);

  const updateDelivery = useCallback((updated) => {
    setDeliveries(prev =>
      prev.map(d => d.id === updated.id ? { ...d, ...updated } : d)
    );
  }, []);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    fetchDeliveries(newDate);
  };

  const activeDeliveries = deliveries.filter(
    d => d.status === 'pending' || d.status === 'en_recojo' || d.status === 'encamino'
  );

  const tabProps = {
    user_id,
    deliveries,
    activeDeliveries,
    loading,
    selectedDate,
    onDateChange: handleDateChange,
    updateDelivery,
    onRefresh:    () => fetchDeliveries(selectedDate),
    // Pass setter down so child tabs can open the map
    // onOpenMap({ delivery, focusOnPickup }) — called by child tabs and detail modal
    onOpenMap: ({ delivery, focusOnPickup = false } = {}) => setMapDelivery({ delivery, focusOnPickup }),
  };

  return (
    <div className="min-h-screen bg-gray-50 font-montserrat flex flex-col">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => setActiveComponent('OptionsDriver')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 shrink-0"
          aria-label="Volver"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-brand-primary leading-tight">
            Entregas Express
          </h1>
          <p className="text-xs text-gray-400">
            {activeDeliveries.length > 0
              ? `${activeDeliveries.length} entrega${activeDeliveries.length > 1 ? 's' : ''} activa${activeDeliveries.length > 1 ? 's' : ''}`
              : 'Sin entregas activas'
            }
          </p>
        </div>
        {newAssignment && (
          <button
            onClick={() => { setNewAssignment(null); setActiveTab('home'); }}
            className="relative p-2 rounded-full bg-brand-primary/10 text-brand-primary animate-bounce"
          >
            <Bell size={18} />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
          </button>
        )}
      </div>

      {/* New assignment banner */}
      {newAssignment && (
        <div className="bg-brand-primary text-white px-4 py-3 flex items-center justify-between">
          <div className="text-sm">
            <div className="font-semibold">¡Nueva entrega asignada!</div>
            <div className="text-xs opacity-80 truncate">{newAssignment.pickup_address}</div>
          </div>
          <button
            onClick={() => setNewAssignment(null)}
            className="text-white/70 hover:text-white text-xs px-2 py-1 shrink-0"
          >
            OK
          </button>
        </div>
      )}

      {/* ── Tab content — overflow-y-auto lives here ── */}
      <div className="flex-1 overflow-y-auto pb-20">
        {activeTab === 'home'     && <DriverExpressHome     {...tabProps} />}
        {activeTab === 'list'     && <DriverExpressList     {...tabProps} />}
        {activeTab === 'earnings' && <DriverExpressEarnings {...tabProps} />}
      </div>

      {/* ── Bottom tab bar ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-10">
        {TABS.map(({ id, label, Icon }) => {
          const isActive  = activeTab === id;
          const showBadge = id === 'home' && activeDeliveries.length > 0;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors relative ${
                isActive ? 'text-brand-primary' : 'text-gray-400'
              }`}
            >
              <div className="relative">
                <Icon size={20} />
                {showBadge && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-brand-primary rounded-full" />
                )}
              </div>
              {label}
              {isActive && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-brand-primary rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Map renders HERE — outside overflow container, above everything ── */}
      {mapDelivery && (
        <DriverExpressMap
          delivery={mapDelivery.delivery}
          user_id={user_id}
          focusOnPickup={mapDelivery.focusOnPickup}
          onClose={() => setMapDelivery(null)}
        />
      )}
    </div>
  );
}